/**
 * TanStack column defs for the org Templates library list. Mirrors the
 * Documents table anatomy (`documentListColumns.tsx`): a pure factory that
 * takes callbacks and returns a `ColumnDef` array, with cell renderers kept
 * declarative. The Name cell composes the shared status glyph
 * (`InlineStatusIndicatorOrIcon` with the `template` item type) plus a subtitle
 * carrying the live compile-stage label and the bound-workflow count.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { format, isToday, parseISO } from "date-fns";

import type { OutputTemplateListItem } from "@/api";
import { InlineStatusIndicatorOrIcon } from "@/components/shared/InlineStatusIndicatorOrIcon";

import { getDocumentTypePill } from "../../utils/libraryItemTypes";
import { TemplateActionsMenu } from "./TemplateActionsMenu";
import { displayStatus } from "./useOutputTemplates";

function formatCreatedAt(value: string | null | undefined): string {
    if (!value) return "—";
    const date = parseISO(value);
    return isToday(date) ? format(date, "h:mm a") : format(date, "MMM d, yyyy");
}

export interface TemplateListColumnsOptions {
    /** Fired by the row's re-roll affordance (failed state only). */
    onRetry: (id: number) => void;
}

export function getTemplateListColumns({
    onRetry,
}: TemplateListColumnsOptions): ColumnDef<OutputTemplateListItem>[] {
    return [
        {
            id: "name",
            accessorKey: "name",
            header: "Name",
            enableSorting: false,
            meta: { cellClassName: "min-w-[220px] max-w-[420px]" },
            cell: ({ row }) => {
                const template = row.original;
                const status = displayStatus(template);
                const progress = template.compile_progress;
                const isCompiling =
                    status === "processing" || status === "pending";
                const boundCount = template.bound_workflow_count;
                return (
                    <div className="flex min-w-0 items-center gap-2.5">
                        <InlineStatusIndicatorOrIcon
                            type="template"
                            status={status}
                            typeKey="document"
                            fileType={template.file_type}
                        />
                        <div className="flex min-w-0 flex-col">
                            <span className="truncate text-sm font-medium">
                                {template.name}
                            </span>
                            <div className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-xs">
                                {isCompiling && progress && (
                                    <span>{progress.label}</span>
                                )}
                                {isCompiling &&
                                    progress &&
                                    progress.total_stages > 0 && (
                                        <span className="text-muted-foreground/70">
                                            Step {progress.stage} of{" "}
                                            {progress.total_stages}
                                        </span>
                                    )}
                                {boundCount > 0 && (
                                    <span>
                                        Used by {boundCount} workflow
                                        {boundCount === 1 ? "" : "s"}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            id: "type",
            header: "Type",
            enableSorting: false,
            // Reuse the canonical Documents/KB type pill: xlsx → emerald
            // "Spreadsheet", pdf → destructive-red "PDF".
            cell: ({ row }) => getDocumentTypePill(row.original.file_type),
        },
        {
            id: "created_at",
            accessorKey: "created_at",
            header: "Date Added",
            enableSorting: false,
            cell: ({ row }) => (
                <span className="text-muted-foreground text-sm">
                    {formatCreatedAt(row.original.created_at)}
                </span>
            ),
        },
        {
            id: "actions",
            header: "",
            enableSorting: false,
            cell: ({ row }) => (
                <div className="flex items-center justify-end">
                    <TemplateActionsMenu
                        template={row.original}
                        onRetry={onRetry}
                    />
                </div>
            ),
        },
    ];
}
